/**
 * 서식 페이지 렌더러.
 *
 * OZ `.ozr` 바이너리를 대체하는 JSON 정의를 화면으로 그린다.
 * 서버의 PDF 템플릿(app/templates/contract.html)과 같은 정의를 소비하므로
 * 화면과 PDF 가 구조적으로 어긋날 수 없다.
 */

import type { TemplatePage } from '@solar/api-client';

import { blockValue, interpolateAddrGu, tableRows } from './template';
import { SignaturePad, type SignatureResult } from './SignaturePad';

interface Props {
  page: TemplatePage;
  data: unknown;
  signatures: Record<string, SignatureResult | null>;
  onSign: (slot: string, result: SignatureResult | null) => void;
  readOnly?: boolean;
}

export function PageRenderer({ page, data, signatures, onSign, readOnly }: Props) {
  // 자치구 라벨({{addr_gu}}) 치환 — 서버 localize_definition 과 동일 규칙.
  // 서버가 이미 치환해 내려주면 멱등이라 아무 일도 하지 않는다.
  const t = (text: string) => interpolateAddrGu(text, data);

  return (
    <section className="page">
      {page.title && <h2 className="page__title">{t(page.title)}</h2>}

      {page.blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <h3 key={index} className="page__heading">
                {t(block.text)}
              </h3>
            );

          case 'paragraph':
            return (
              <p key={index} className="page__para">
                {t(block.text)}
              </p>
            );

          case 'fields':
            return (
              <table key={index} className="kv">
                <tbody>
                  {blockValue(block, data).map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      <td>{row.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );

          case 'table': {
            const rows = tableRows(block, data);
            return (
              <table key={index} className="kv">
                <thead>
                  <tr>
                    {block.columns.map((c) => (
                      <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                        {t(c.label)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={block.columns.length}>-</td>
                    </tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={i}>
                        {block.columns.map((c) => (
                          <td key={c.key}>{row[c.key]}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            );
          }

          case 'signature':
            return (
              <SignaturePad
                key={block.slot}
                label={block.label ? t(block.label) : '서명'}
                value={signatures[block.slot] ?? null}
                onChange={(result) => onSign(block.slot, result)}
                disabled={readOnly}
              />
            );

          default:
            return null;
        }
      })}
    </section>
  );
}
